import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { QuoteDetails, SelectedProduct, QuoteCalculations, PlanType, PartnershipDetails } from '../types'
import { PORTAL_PRICING, PLAN_PRICING } from '../data'
import { showToast } from '@store/uiStore'

// Helper to format currency with 2 decimals
const formatCurrencyDecimal = (value: number, currency: string): string => {
  const code = currency || 'AUD'
  const symbol = code === 'AUD' ? 'A$' : code === 'USD' ? '$' : code === 'NZD' ? 'NZ$' : code === 'EUR' ? '€' : code === 'CAD' ? 'C$' : '$'
  return symbol + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Helper to format currency without decimals
const formatCurrency = (value: number, currency: string): string => {
  const code = currency || 'AUD'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

// Helper to format number with commas
const formatNumber = (value: number): string => {
  return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

interface ExportData {
  details: QuoteDetails
  selectedProducts: SelectedProduct[]
  calculations: QuoteCalculations | null
  partnership?: PartnershipDetails
}

/**
 * Export quote to PDF - Professional format matching the new design
 */
export function exportQuoteToPDF({ details, calculations }: ExportData): void {
  const doc = new jsPDF()
  const customerName = details.customerName || 'Customer Name'
  const preparedBy = details.preparedBy || 'Sales Representative'
  const startDate = details.startDate || new Date().toISOString().split('T')[0]
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  let yPos = 20

  // Title - "Frankieone Quote for [Customer Name]"
  doc.setFontSize(16)
  doc.setFont('helvetica', 'bold')
  doc.text(`Frankieone Quote for ${customerName}`, 14, yPos)
  yPos += 7

  // Subtitle
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.text(`Quote prepared by ${preparedBy} on ${today}`, 14, yPos)
  yPos += 15

  // Proposal header table
  autoTable(doc, {
    startY: yPos,
    head: [['Start date', 'Term', 'Payment', 'Invoicing', 'Auto-renew']],
    body: [[
      startDate,
      `${details.contractTermMonths} months`,
      details.paymentFrequency,
      'In arrears, payment due within 14 days',
      details.autoRenew ? 'Yes' : 'No'
    ]],
    theme: 'grid',
    headStyles: {
      fillColor: [68, 114, 196],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold',
      halign: 'left'
    },
    bodyStyles: {
      fillColor: [180, 199, 231],
      textColor: 0,
      fontSize: 9
    },
    margin: { left: 14, right: 14 }
  })

  // Get the final Y position after the table
  // @ts-expect-error - jspdf-autotable adds lastAutoTable to doc
  yPos = doc.lastAutoTable.finalY + 15

  // Upfront payment section
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.text('Upfront payment:', 14, yPos)
  yPos += 5

  // Calculate values for upfront payment table
  const portalPrice = PORTAL_PRICING[details.portalSeats]?.price ?? 0
  const childAccountsFee = details.childAccounts * 2400

  // Build upfront items table
  const upfrontItems: (string | number)[][] = []

  // Annual package
  upfrontItems.push([
    `Annual ${details.customerPlan} package, comprising:`,
    formatNumber(details.platformFees),
    '',
    '-',
    formatNumber(details.platformFees)
  ])

  // Portal seats
  if (portalPrice > 0) {
    upfrontItems.push([
      '- Case Manager Portal (seats)',
      formatNumber(portalPrice),
      details.portalSeats.toString(),
      '-',
      formatNumber(portalPrice)
    ])
  }

  // Child accounts
  if (details.childAccounts > 0) {
    upfrontItems.push([
      '- child accounts',
      '$2,400',
      details.childAccounts.toString(),
      '-',
      formatNumber(childAccountsFee)
    ])
  } else {
    upfrontItems.push([
      '- child accounts',
      '-',
      '-',
      '-',
      '-'
    ])
  }

  // Support level
  const supportText = details.supportLevel === 0 ? 'Basic (Access + Ticketing)' : 'Enhanced (Allocated CSM + TechOps)'
  const supportQty = details.supportLevel > 0 ? '1' : '-'
  upfrontItems.push([
    `- Support level: ${supportText}`,
    details.supportLevel === 0 ? '-' : formatNumber(details.supportLevel),
    supportQty,
    '-',
    details.supportLevel === 0 ? '-' : formatNumber(details.supportLevel)
  ])

  // Implementation support
  const implSupportText = details.implementationSupport === 0 ? 'No' : 'Yes'
  const implSupportQty = details.implementationSupport > 0 ? '1' : '-'
  upfrontItems.push([
    `- Additional implementation support: ${implSupportText}`,
    details.implementationSupport === 0 ? '-' : formatNumber(details.implementationSupport),
    implSupportQty,
    '-',
    details.implementationSupport === 0 ? '-' : formatNumber(details.implementationSupport)
  ])

  // Hosted OneSDK
  const hostedSDKText = details.hostedOneSDK === 0 ? 'No' : 'Yes'
  const hostedSDKQty = details.hostedOneSDK > 0 ? '1' : '-'
  upfrontItems.push([
    `- Hosted OneSDK: ${hostedSDKText}`,
    details.hostedOneSDK === 0 ? '-' : formatNumber(details.hostedOneSDK),
    hostedSDKQty,
    '-',
    details.hostedOneSDK === 0 ? '-' : formatNumber(details.hostedOneSDK)
  ])

  // Implementation fee
  upfrontItems.push([
    '- Implementation fee (one-off upfront standard package)',
    formatNumber(details.implementationFee),
    '',
    '-',
    formatNumber(details.implementationFee)
  ])

  // Minimum commitment fees
  upfrontItems.push([
    'Minimum commitment fees',
    formatNumber(details.minimumCommitment),
    '1',
    '-',
    formatNumber(details.minimumCommitment)
  ])

  // Upfront items table
  autoTable(doc, {
    startY: yPos,
    head: [['Item', 'Price', 'Qty', 'Discount', 'Total Cost']],
    body: upfrontItems,
    theme: 'grid',
    headStyles: {
      fillColor: [47, 85, 151],
      textColor: 255,
      fontSize: 8,
      fontStyle: 'bold',
      halign: 'right'
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 2
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: 85 },
      1: { halign: 'right', cellWidth: 30 },
      2: { halign: 'right', cellWidth: 15 },
      3: { halign: 'right', cellWidth: 20 },
      4: { halign: 'right', cellWidth: 32 }
    },
    margin: { left: 14, right: 14 }
  })

  // @ts-expect-error - jspdf-autotable adds lastAutoTable to doc
  yPos = doc.lastAutoTable.finalY + 5

  // Calculate upfront total
  const upfrontTotal = details.platformFees +
    portalPrice +
    childAccountsFee +
    details.supportLevel +
    details.implementationSupport +
    details.hostedOneSDK +
    details.implementationFee +
    details.minimumCommitment

  // Upfront payment total
  yPos += 3
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.text('Upfront payment', 14, yPos)
  doc.text('Total Price', 160, yPos)
  doc.text(formatNumber(upfrontTotal), 196, yPos, { align: 'right' })
  yPos += 15

  // Annual Totals by Year section (only if contract >= 12 months)
  if (details.contractTermMonths >= 12) {
    // Check if we need a new page
    if (yPos > 200) {
      doc.addPage()
      yPos = 20
    }

    // Section title
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.text('Annual spend by year:', 14, yPos)
    yPos += 5

    // Calculate number of years
    const numYears = Math.floor(details.contractTermMonths / 12)

    // Annual recurring fees
    const annualRecurringFees = details.platformFees + portalPrice + details.supportLevel + details.hostedOneSDK + childAccountsFee

    // Transaction amount: use MAX of transaction revenue or minimum commitment
    const transactionAmount = calculations
      ? Math.max(calculations.transactionRevenue, details.minimumCommitment)
      : details.minimumCommitment

    // Build table rows
    const annualRows: string[][] = []
    for (let year = 1; year <= numYears; year++) {
      let yearTotal: number
      if (year === 1) {
        // Year 1 includes implementation fees (one-time)
        yearTotal = annualRecurringFees + transactionAmount + details.implementationFee + details.implementationSupport
      } else {
        // Year 2+ is just recurring fees + transaction amount
        yearTotal = annualRecurringFees + transactionAmount
      }

      annualRows.push([
        `Year ${year}${year === 1 ? ' (incl. implementation)' : ''}`,
        formatNumber(yearTotal)
      ])
    }

    // Create the table
    autoTable(doc, {
      startY: yPos,
      head: [['Year', 'Total annual spend']],
      body: annualRows,
      theme: 'grid',
      headStyles: {
        fillColor: [68, 114, 196],
        textColor: 255,
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'left'
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3
      },
      columnStyles: {
        0: { cellWidth: 120, fontStyle: 'bold' },
        1: { cellWidth: 62, halign: 'right' }
      },
      margin: { left: 14, right: 14 }
    })

    // @ts-expect-error - jspdf-autotable adds lastAutoTable to doc
    yPos = doc.lastAutoTable.finalY + 10
  }

  // Check if we need a new page for footer
  if (yPos > 240) {
    doc.addPage()
    yPos = 20
  }

  // Footer
  yPos += 5
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.rect(14, yPos, 182, 10)
  doc.text('Quote valid for 30 days upon receipt', 16, yPos + 6)

  // Save the PDF
  const fileName = `quote_${customerName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
  doc.save(fileName)
}

/**
 * Export quote to CSV
 */
export function exportQuoteToCSV({ details, selectedProducts, calculations }: ExportData): void {
  const { displayCurrency } = details

  let csv = 'Deal Return Calculator Export\n\n'
  csv += 'DEAL DETAILS\n'
  csv += `Customer Name,${details.customerName || 'N/A'}\n`
  csv += `Prepared By,${details.preparedBy || 'N/A'}\n`
  csv += `Plan,${details.customerPlan}\n`
  csv += `Platform Fees,${details.platformFees}\n`
  csv += `Implementation Fee,${details.implementationFee}\n`
  csv += `Contract Term,${details.contractTermMonths} months\n`
  csv += `Payment Frequency,${details.paymentFrequency}\n\n`

  if (calculations) {
    csv += 'KEY METRICS\n'
    csv += `MRR,${formatCurrency(calculations.mrr, displayCurrency)}\n`
    csv += `ARR,${formatCurrency(calculations.arr, displayCurrency)}\n`
    csv += `TCV,${formatCurrency(calculations.tcv, displayCurrency)}\n`
    csv += `Gross Margin,${calculations.grossProfitMargin.toFixed(1)}%\n`
    csv += `LTV/CAC,${calculations.ltvCacRatio.toFixed(1)}x\n`
    csv += `Payback,${calculations.paybackMonths.toFixed(1)} months\n\n`
  }

  csv += 'PRODUCTS\n'
  csv += 'Name,Currency,Volume,Price,Annual Total\n'
  selectedProducts.forEach((p) => {
    const annualTotal = p.volume * p.effectivePrice
    csv += `"${p.name}",${displayCurrency},${p.volume},${p.effectivePrice},${annualTotal}\n`
  })

  const blob = new Blob([csv], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `quote_${(details.customerName || 'customer').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`
  a.click()
  window.URL.revokeObjectURL(url)
}

/**
 * Export usage report to PDF - Professional format with blue header and tables
 */
export function exportUsageToPDF({ details, selectedProducts }: ExportData): void {
  if (selectedProducts.length === 0) {
    showToast('No products selected. Add products to export usage.', 'warning')
    return
  }

  const doc = new jsPDF()
  const customerName = details.customerName || 'Customer Name'
  const { displayCurrency } = details

  // Blue header background
  doc.setFillColor(47, 85, 151)
  doc.rect(0, 0, 210, 45, 'F')

  // Header text
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(24)
  doc.setFont('helvetica', 'bold')
  doc.text('Estimated Usage Report', 14, 25)

  doc.setFontSize(12)
  doc.setFont('helvetica', 'normal')
  doc.text(customerName, 14, 38)

  // Reset text color
  doc.setTextColor(0, 0, 0)

  let yPos = 60

  // Section title
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text('PRODUCT SEARCHES (TRANSACTION PRODUCTS)', 14, yPos)
  yPos += 8

  // Build product rows for table
  const productRows: string[][] = []
  let totalMonthlySpend = 0

  selectedProducts.forEach((p) => {
    const monthlyTotal = (p.volume * p.effectivePrice) / 12
    totalMonthlySpend += monthlyTotal

    productRows.push([
      p.name,
      displayCurrency,
      formatCurrencyDecimal(p.effectivePrice, displayCurrency),
      p.volume.toLocaleString(),
      formatCurrencyDecimal(monthlyTotal, displayCurrency)
    ])
  })

  // Products table
  autoTable(doc, {
    startY: yPos,
    head: [['Product Name', 'Currency', 'Price', 'FX Est. Annual Volume', 'Total Est Monthly Cost']],
    body: productRows,
    theme: 'grid',
    headStyles: {
      fillColor: [47, 85, 151],
      textColor: 255,
      fontSize: 8,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 3
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: 70 },
      1: { halign: 'center', cellWidth: 25 },
      2: { halign: 'right', cellWidth: 25 },
      3: { halign: 'right', cellWidth: 35 },
      4: { halign: 'right', cellWidth: 35 }
    },
    margin: { left: 14, right: 14 }
  })

  // @ts-expect-error - jspdf-autotable adds lastAutoTable to doc
  yPos = doc.lastAutoTable.finalY + 15

  // Calculate annual total
  const totalAnnualSpend = selectedProducts.reduce((sum, p) => sum + (p.volume * p.effectivePrice), 0)

  // Total section
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.text('TOTAL ESTIMATED ANNUAL USAGE SPEND', 14, yPos)
  doc.text(formatCurrencyDecimal(totalAnnualSpend, displayCurrency), 196, yPos, { align: 'right' })

  yPos += 15

  // Footer
  doc.setFontSize(9)
  doc.setFont('helvetica', 'italic')
  doc.text('Quote valid for 30 days upon receipt', 14, yPos)

  // Save the PDF
  doc.save(`usage_${customerName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`)
}

/**
 * Export usage report to CSV
 */
export function exportUsageToCSV({ details, selectedProducts }: ExportData): void {
  if (selectedProducts.length === 0) {
    showToast('No products selected. Add products to export usage.', 'warning')
    return
  }

  const { displayCurrency } = details

  let csv = 'ESTIMATED USAGE REPORT\n'
  csv += `Customer Name,${details.customerName || 'Customer'}\n`
  csv += `Date,${new Date().toLocaleDateString()}\n\n`
  csv += 'PRODUCT USAGE\n'
  csv += 'Product,Annual Volume,Unit Price,Annual Cost\n'

  let grandTotal = 0
  selectedProducts.forEach((p) => {
    const annualTotal = p.volume * p.effectivePrice
    grandTotal += annualTotal
    csv += `"${p.name}",${p.volume},${p.effectivePrice},${annualTotal}\n`
  })

  csv += `\nTotal,,,"${formatCurrency(grandTotal, displayCurrency)}"\n`

  const blob = new Blob([csv], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `usage_${(details.customerName || 'customer').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`
  a.click()
  window.URL.revokeObjectURL(url)
}

/**
 * Export 3-plan comparison PDF - Full implementation with colored tables
 */
export function exportComparisonPDF({ details, selectedProducts }: ExportData): void {
  const doc = new jsPDF()
  const { displayCurrency, contractTermMonths } = details
  const preparedBy = details.preparedBy || 'N/A'
  const today = new Date().toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })

  let yPos = 20

  // Title
  doc.setFontSize(20)
  doc.setFont('helvetica', 'bold')
  doc.text('FrankieOne Deal Comparison', 14, yPos)
  yPos += 10

  // Metadata
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.text(`Contract Term: ${contractTermMonths} months`, 14, yPos)
  yPos += 5
  doc.text(`Currency: ${displayCurrency}`, 14, yPos)
  yPos += 5
  doc.text(`Date: ${today}`, 14, yPos)
  yPos += 5
  doc.text(`Prepared by: ${preparedBy}`, 14, yPos)
  yPos += 12

  // Plan comparison data
  const plans: PlanType[] = ['Basic', 'Professional', 'Enterprise']

  // Calculate monthly transaction totals for each plan
  const getProductTotalForPlan = (plan: PlanType): number => {
    return selectedProducts.reduce((sum, p) => {
      let price: number
      switch (plan) {
        case 'Basic': price = p.basePrice; break
        case 'Professional': price = p.proPrice; break
        case 'Enterprise': price = p.enterprisePrice; break
        default: price = p.effectivePrice
      }
      return sum + (p.volume * price) / 12
    }, 0)
  }

  // Main comparison table data
  const comparisonData = [
    ['Platform Fee (Monthly)',
      formatCurrencyDecimal(PLAN_PRICING.Basic.platformFees / 12, displayCurrency),
      formatCurrencyDecimal(PLAN_PRICING.Professional.platformFees / 12, displayCurrency),
      formatCurrencyDecimal(PLAN_PRICING.Enterprise.platformFees / 12, displayCurrency)
    ],
    ['Minimum Commitment',
      formatCurrencyDecimal(PLAN_PRICING.Basic.minimumCommitment, displayCurrency),
      formatCurrencyDecimal(PLAN_PRICING.Professional.minimumCommitment, displayCurrency),
      formatCurrencyDecimal(PLAN_PRICING.Enterprise.minimumCommitment, displayCurrency)
    ],
    ['Monthly Transactions',
      formatCurrencyDecimal(getProductTotalForPlan('Basic'), displayCurrency),
      formatCurrencyDecimal(getProductTotalForPlan('Professional'), displayCurrency),
      formatCurrencyDecimal(getProductTotalForPlan('Enterprise'), displayCurrency)
    ],
    ['Total Monthly',
      formatCurrencyDecimal(PLAN_PRICING.Basic.platformFees / 12 + getProductTotalForPlan('Basic'), displayCurrency),
      formatCurrencyDecimal(PLAN_PRICING.Professional.platformFees / 12 + getProductTotalForPlan('Professional'), displayCurrency),
      formatCurrencyDecimal(PLAN_PRICING.Enterprise.platformFees / 12 + getProductTotalForPlan('Enterprise'), displayCurrency)
    ],
    ['Implementation Fee\n(One-time)',
      formatCurrencyDecimal(PLAN_PRICING.Basic.implementationFee, displayCurrency),
      formatCurrencyDecimal(PLAN_PRICING.Professional.implementationFee, displayCurrency),
      formatCurrencyDecimal(PLAN_PRICING.Enterprise.implementationFee, displayCurrency)
    ],
    ['Contract Term',
      `${contractTermMonths} months`,
      `${contractTermMonths} months`,
      `${contractTermMonths} months`
    ],
    ['TCV (Total Contract Value)',
      formatCurrencyDecimal(
        (PLAN_PRICING.Basic.platformFees + Math.max(getProductTotalForPlan('Basic') * 12, PLAN_PRICING.Basic.minimumCommitment)) * (contractTermMonths / 12) + PLAN_PRICING.Basic.implementationFee,
        displayCurrency
      ),
      formatCurrencyDecimal(
        (PLAN_PRICING.Professional.platformFees + Math.max(getProductTotalForPlan('Professional') * 12, PLAN_PRICING.Professional.minimumCommitment)) * (contractTermMonths / 12) + PLAN_PRICING.Professional.implementationFee,
        displayCurrency
      ),
      formatCurrencyDecimal(
        (PLAN_PRICING.Enterprise.platformFees + Math.max(getProductTotalForPlan('Enterprise') * 12, PLAN_PRICING.Enterprise.minimumCommitment)) * (contractTermMonths / 12) + PLAN_PRICING.Enterprise.implementationFee,
        displayCurrency
      )
    ]
  ]

  // Main comparison table with colored columns
  autoTable(doc, {
    startY: yPos,
    head: [['Item', 'Basic', 'Professional', 'Enterprise']],
    body: comparisonData,
    theme: 'grid',
    headStyles: {
      fillColor: [68, 114, 196],
      textColor: 255,
      fontSize: 10,
      fontStyle: 'bold',
      halign: 'center'
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 4
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: 50, fontStyle: 'bold' },
      1: { halign: 'right', cellWidth: 40, fillColor: [198, 224, 180] }, // Light green
      2: { halign: 'right', cellWidth: 45, fillColor: [189, 215, 238] }, // Light blue
      3: { halign: 'right', cellWidth: 45, fillColor: [255, 242, 204] }  // Light yellow
    },
    margin: { left: 14, right: 14 }
  })

  // @ts-expect-error - jspdf-autotable adds lastAutoTable to doc
  yPos = doc.lastAutoTable.finalY + 15

  // Selected Products section (if any products selected)
  if (selectedProducts.length > 0) {
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('Selected Products:', 14, yPos)
    yPos += 5

    // Products pricing table
    const productPricingData = selectedProducts.map(p => [
      p.name,
      p.volume.toLocaleString(),
      formatCurrencyDecimal(p.basePrice, displayCurrency),
      formatCurrencyDecimal(p.proPrice, displayCurrency),
      formatCurrencyDecimal(p.enterprisePrice, displayCurrency)
    ])

    autoTable(doc, {
      startY: yPos,
      head: [['Product', 'Volume', 'Basic Price', 'Professional Price', 'Enterprise Price']],
      body: productPricingData,
      theme: 'grid',
      headStyles: {
        fillColor: [68, 114, 196],
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 2
      },
      columnStyles: {
        0: { halign: 'left', cellWidth: 60 },
        1: { halign: 'right', cellWidth: 25 },
        2: { halign: 'right', cellWidth: 30, fillColor: [198, 224, 180] },
        3: { halign: 'right', cellWidth: 35, fillColor: [189, 215, 238] },
        4: { halign: 'right', cellWidth: 35, fillColor: [255, 242, 204] }
      },
      margin: { left: 14, right: 14 }
    })

    // @ts-expect-error - jspdf-autotable adds lastAutoTable to doc
    yPos = doc.lastAutoTable.finalY + 10

    // Product totals by plan
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('Product Totals by Plan:', 14, yPos)
    yPos += 5

    const productTotalsData = selectedProducts.map(p => [
      p.name,
      formatCurrencyDecimal(p.volume * p.basePrice, displayCurrency),
      formatCurrencyDecimal(p.volume * p.proPrice, displayCurrency),
      formatCurrencyDecimal(p.volume * p.enterprisePrice, displayCurrency)
    ])

    // Add totals row
    productTotalsData.push([
      'TOTAL',
      formatCurrencyDecimal(selectedProducts.reduce((sum, p) => sum + p.volume * p.basePrice, 0), displayCurrency),
      formatCurrencyDecimal(selectedProducts.reduce((sum, p) => sum + p.volume * p.proPrice, 0), displayCurrency),
      formatCurrencyDecimal(selectedProducts.reduce((sum, p) => sum + p.volume * p.enterprisePrice, 0), displayCurrency)
    ])

    autoTable(doc, {
      startY: yPos,
      head: [['Product', 'Basic Total', 'Professional Total', 'Enterprise Total']],
      body: productTotalsData,
      theme: 'grid',
      headStyles: {
        fillColor: [68, 114, 196],
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 2
      },
      columnStyles: {
        0: { halign: 'left', cellWidth: 70 },
        1: { halign: 'right', cellWidth: 35, fillColor: [198, 224, 180] },
        2: { halign: 'right', cellWidth: 40, fillColor: [189, 215, 238] },
        3: { halign: 'right', cellWidth: 40, fillColor: [255, 242, 204] }
      },
      margin: { left: 14, right: 14 },
      didParseCell: (data) => {
        // Bold the TOTAL row
        if (data.row.index === productTotalsData.length - 1) {
          data.cell.styles.fontStyle = 'bold'
        }
      }
    })

    // @ts-expect-error - jspdf-autotable adds lastAutoTable to doc
    yPos = doc.lastAutoTable.finalY + 10
  }

  // Check if we need a new page for annual totals
  if (yPos > 220) {
    doc.addPage()
    yPos = 20
  }

  // Annual Totals by Year
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text('Annual Totals by Year:', 14, yPos)
  yPos += 5

  const numYears = Math.floor(contractTermMonths / 12)
  const annualTotalsData: string[][] = []

  for (let year = 1; year <= numYears; year++) {
    const row = [`Year ${year}${year === 1 ? ' (incl. Implementation)' : ''}`]

    plans.forEach(plan => {
      const planPricing = PLAN_PRICING[plan]
      const annualTransactions = Math.max(getProductTotalForPlan(plan) * 12, planPricing.minimumCommitment)
      let yearTotal = planPricing.platformFees + annualTransactions

      if (year === 1) {
        yearTotal += planPricing.implementationFee
      }

      row.push(formatCurrencyDecimal(yearTotal, displayCurrency))
    })

    annualTotalsData.push(row)
  }

  autoTable(doc, {
    startY: yPos,
    head: [['Year', 'Basic', 'Professional', 'Enterprise']],
    body: annualTotalsData,
    theme: 'grid',
    headStyles: {
      fillColor: [68, 114, 196],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold',
      halign: 'center'
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 3
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: 60, fontStyle: 'bold' },
      1: { halign: 'right', cellWidth: 40, fillColor: [198, 224, 180] },
      2: { halign: 'right', cellWidth: 45, fillColor: [189, 215, 238] },
      3: { halign: 'right', cellWidth: 40, fillColor: [255, 242, 204] }
    },
    margin: { left: 14, right: 14 }
  })

  // Save the PDF
  const fileName = `comparison_${(details.customerName || 'deal').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
  doc.save(fileName)
}

/**
 * Export Reseller Proposal PDF - Professional format matching the reseller proposal template
 */
export function exportResellerProposalPDF({ details, selectedProducts, partnership }: ExportData): void {
  if (!partnership || !partnership.isReseller) {
    showToast('Please enable Reseller Proposal in the Partnerships tab first.', 'warning')
    return
  }

  const doc = new jsPDF()
  const customerName = details.customerName || 'Partner Name'
  const proposalYear = partnership.proposalYear || new Date().getFullYear()

  let yPos = 20

  // Title - Blue header bar (matching quote PDF branding)
  doc.setFillColor(68, 114, 196) // Blue matching quote PDF
  doc.rect(14, yPos - 8, 182, 18, 'F')

  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(255, 255, 255) // White text on blue background
  doc.text(`${proposalYear} ${customerName} Reseller Proposal`, 105, yPos + 2, { align: 'center' })
  doc.setTextColor(0, 0, 0) // Reset text color

  yPos += 18

  // Subtitle
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.text(`This ${proposalYear} Reseller proposal for ${details.contractTermMonths} months.`, 14, yPos)
  yPos += 10

  // ==================== TERMS SECTION ====================
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(47, 85, 151) // Dark blue matching quote PDF
  doc.text('Terms', 14, yPos)
  doc.setTextColor(0, 0, 0) // Reset
  yPos += 5

  const termsData = [
    ['Contract Length', `${details.contractTermMonths} months`],
    ['Implementation Fee Billing', partnership.terms.implementationFeeBilling],
    ['Licence Fee Billing PA', partnership.terms.licenceFeeBilling],
    ['Minimum Commitment Billing PA', partnership.terms.minimumCommitmentBilling],
    ['*Transaction Fee Billing', partnership.terms.transactionFeeBilling],
    ['*Payment Terms', `${partnership.terms.paymentTermsDays} days upon invoice`],
  ]

  autoTable(doc, {
    startY: yPos,
    body: termsData,
    theme: 'grid',
    styles: {
      fontSize: 9,
      cellPadding: 3,
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 60 },
      1: { cellWidth: 122, halign: 'right' },
    },
    margin: { left: 14, right: 14 },
  })

  // @ts-expect-error - jspdf-autotable adds lastAutoTable to doc
  yPos = doc.lastAutoTable.finalY + 10

  // ==================== TRANSACTION FEES SECTION ====================
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(47, 85, 151) // Dark blue matching quote PDF
  doc.text('Transaction Fees (AUD ex GST)', 14, yPos)
  doc.setTextColor(0, 0, 0) // Reset
  yPos += 5

  // Build transaction fees from selected products
  const transactionFeesData = selectedProducts.map(p => [
    p.name,
    formatCurrencyDecimal(p.effectivePrice, 'AUD'),
  ])

  if (transactionFeesData.length > 0) {
    autoTable(doc, {
      startY: yPos,
      body: transactionFeesData,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 62, halign: 'right' },
      },
      margin: { left: 14, right: 14 },
    })

    // @ts-expect-error - jspdf-autotable adds lastAutoTable to doc
    yPos = doc.lastAutoTable.finalY + 10
  } else {
    doc.setFontSize(9)
    doc.setFont('helvetica', 'italic')
    doc.text('No transaction products selected', 14, yPos)
    yPos += 10
  }

  // ==================== FIXED COSTS SECTION ====================
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(47, 85, 151) // Dark blue matching quote PDF
  doc.text('Fixed Costs', 14, yPos)
  doc.setTextColor(0, 0, 0) // Reset
  yPos += 5

  const implementationFee = details.implementationFee
  const annualLicenseFeeText = `${formatCurrency(partnership.annualLicenseFee, 'AUD')} (${partnership.licensePlan.type})`
  const annualMinCommitment = formatCurrency(partnership.annualMinimumCommitment, 'AUD')

  const fixedCostsData = [
    ['One-off Implementation fee', 'Annual License Fee', 'Annual Minimum Commitment'],
    [formatCurrency(implementationFee, 'AUD'), annualLicenseFeeText, annualMinCommitment],
  ]

  autoTable(doc, {
    startY: yPos,
    head: [fixedCostsData[0]],
    body: [fixedCostsData[1]],
    theme: 'grid',
    headStyles: {
      fillColor: [47, 85, 151], // Dark blue matching quote PDF
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center',
    },
    bodyStyles: {
      fillColor: [180, 199, 231], // Light blue matching quote PDF
      fontSize: 9,
      halign: 'center',
    },
    margin: { left: 14, right: 14 },
  })

  // @ts-expect-error - jspdf-autotable adds lastAutoTable to doc
  yPos = doc.lastAutoTable.finalY + 5

  // Transaction fee footnote
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.text('*Transaction fees will be drawn down against a prepaid Monthly Minimum Commitment until the balance is exhausted, after which', 14, yPos)
  yPos += 4
  doc.text('standard fees shall apply.', 14, yPos)
  yPos += 8

  // ==================== NOTES SECTION ====================
  if (partnership.notes.length > 0) {
    partnership.notes.forEach(note => {
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.text(`• ${note}`, 14, yPos, { maxWidth: 180 })
      yPos += 5
    })
    yPos += 5
  }

  // Check if we need a new page
  if (yPos > 200) {
    doc.addPage()
    yPos = 20
  }

  // ==================== IMPLEMENTATION FEE SECTION ====================
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(47, 85, 151) // Dark blue matching quote PDF
  doc.text('Implementation Fee', 14, yPos)
  doc.setTextColor(0, 0, 0) // Reset
  yPos += 8

  // Implementation band header with price
  doc.setFillColor(180, 199, 231) // Light blue matching quote PDF
  doc.rect(14, yPos - 4, 182, 12, 'F')
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.text(partnership.implementationBand, 16, yPos + 3)
  const implFeeText = `${formatCurrency(implementationFee, 'AUD')} ex GST. Time and materials at ${formatCurrency(partnership.blendedResourceRate, 'AUD')}/day.`
  doc.setFont('helvetica', 'normal')
  doc.text(implFeeText, 194, yPos + 3, { align: 'right' })
  yPos += 14

  // "In Scope" header
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.text('In Scope:', 14, yPos)
  yPos += 6

  // Scope items as bullet list
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')

  const scopeItems: string[] = []
  if (partnership.implementationScope.frankieOnePortal) {
    scopeItems.push('FrankieOne Portal')
  }
  if (partnership.implementationScope.sdkRecommendations) {
    scopeItems.push('SDK Recommendations')
  }
  if (partnership.implementationScope.apiIntegrationSupport) {
    scopeItems.push('API Integration Support & Architecture Guidance')
  }
  if (partnership.implementationScope.smeKycIdvKybRecipes) {
    scopeItems.push('SME KYC, IDV, KYB Recipes')
  }
  if (partnership.implementationScope.standardRecipesWorkflows) {
    scopeItems.push('Standard Recipes/workflows from 4 out of the box templates')
  }
  if (partnership.implementationScope.serviceAdviceSelection) {
    scopeItems.push('Service Advice Selection from Partner Ecosystem')
  }
  if (partnership.implementationScope.childAccountClone) {
    scopeItems.push(`${partnership.implementationScope.childAccountCloneCount} (minimum number) standard child clone from parent Child Accounts`)
  }
  if (partnership.implementationScope.hyperCareMonitoring) {
    scopeItems.push(`Hyper care and performance monitoring after Go Live (${partnership.implementationScope.hyperCareDays} days over ${partnership.implementationScope.hyperCareWeeks} weeks)`)
  }
  if (partnership.implementationScope.portalTraining) {
    scopeItems.push('Portal Training (Train the trainer)')
  }

  scopeItems.forEach(item => {
    doc.text(`•  ${item}`, 16, yPos)
    yPos += 5
  })

  yPos += 8

  // Check if we need a new page for license plan
  if (yPos > 180) {
    doc.addPage()
    yPos = 20
  }

  // ==================== LICENSE PLAN SECTION (Card Style) ====================
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(47, 85, 151) // Dark blue matching quote PDF
  doc.text('License Plan', 14, yPos)
  doc.setTextColor(0, 0, 0) // Reset
  yPos += 8

  // Draw card box
  const cardX = 14
  const cardY = yPos
  const cardWidth = 80
  const cardHeight = 85

  // Card border
  doc.setDrawColor(68, 114, 196) // Blue matching quote PDF
  doc.setLineWidth(1)
  doc.rect(cardX, cardY, cardWidth, cardHeight)

  // Plan type header (bold, large)
  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.text(partnership.licensePlan.type, cardX + cardWidth / 2, cardY + 12, { align: 'center' })

  // Price per annum button style
  doc.setFillColor(68, 114, 196) // Blue matching quote PDF header
  doc.roundedRect(cardX + 10, cardY + 18, cardWidth - 20, 10, 5, 5, 'F')
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(255, 255, 255)
  doc.text(`${formatCurrency(partnership.annualLicenseFee, 'AUD')} p.a.`, cardX + cardWidth / 2, cardY + 25, { align: 'center' })
  doc.setTextColor(0, 0, 0)

  // Features list with checkmarks
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  let featureY = cardY + 35

  const features = [
    `${partnership.licensePlan.features.rulesets} ruleset`,
    `${partnership.licensePlan.features.portalSeats} portal seat`,
    `${partnership.licensePlan.features.uatSandboxAccounts} UAT/Sandbox account`,
    `${partnership.licensePlan.features.childAccounts} child account`,
    partnership.licensePlan.features.evidenceCapture ? 'Evidence capture' : null,
    partnership.licensePlan.features.apiAccess ? 'API access' : null,
    partnership.licensePlan.features.basicHostedOneSDK ? 'Basic hosted OneSDK' : null,
  ].filter(f => f !== null) as string[]

  features.forEach(feature => {
    // Blue checkmark circle (matching quote PDF branding)
    doc.setFillColor(68, 114, 196) // Blue matching quote PDF
    doc.circle(cardX + 8, featureY - 1.5, 2.5, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(6)
    doc.text('✓', cardX + 6.5, featureY)
    doc.setTextColor(0, 0, 0)
    doc.setFontSize(9)
    doc.text(feature, cardX + 14, featureY)
    featureY += 6
  })

  // Price next to the card
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(47, 85, 151) // Dark blue matching quote PDF
  doc.text(formatCurrency(partnership.annualLicenseFee, 'AUD'), cardX + cardWidth + 20, cardY + 15)
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(0, 0, 0) // Reset
  doc.text('per annum', cardX + cardWidth + 20, cardY + 22)

  yPos = cardY + cardHeight + 15

  // Footer with blue styling
  doc.setFillColor(180, 199, 231) // Light blue matching quote PDF
  doc.rect(14, yPos, 182, 10, 'F')
  doc.setFontSize(9)
  doc.setFont('helvetica', 'italic')
  doc.text('Quote valid for 30 days upon receipt', 16, yPos + 6)

  // Save the PDF
  const fileName = `reseller_proposal_${customerName.replace(/\s+/g, '_')}_${proposalYear}.pdf`
  doc.save(fileName)
}
